import { toast } from 'sonner';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Grid, Paper } from '@mui/material';
import { getDoc, doc, writeBatch } from 'firebase/firestore';

import { db } from '../../firebase-config';
import { useAuth, useAnalytics } from '../../hooks';
import PaymentDetails from './misc/payment-details';
import PaymentContext from './PaymentContext';
import { TREATMENT_TYPES, getCatalogueId, processFieldData } from '../../utils';
import PaymentMaintenance from './misc/PaymentMaintenance';
import PaymentProcessorContainer from './misc/PaymentProcessorContainer';
import { getPaymentProcessor } from './paymentHelpers';
import CustomPaymentContainer from './misc/CustomPaymentContainer';
import { ScriptOnlyCheckout } from './misc/ScriptOnlyCheckout';

/**
 *
 * @param {string} formName
 * @returns {import('@shared/forms/form.types').HealthCategory | undefined}
 */
const getHealthCategory = (formName) => {
  if (!formName) {
    return undefined;
  }

  return /** @type {import('@shared/forms/form.types').HealthCategory} */ (formName);
};

const excludedTypes = ['consult', 'booking fee', 'substitution fee'];
const { MEDICINAL_CANNABIS } = TREATMENT_TYPES;

const styles = {
  paymentContainerGrid: {
    textAlign: 'center',
    borderRadius: '5px',
    maxHeight: '95dvh',
    width: '100%',
    padding: '0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    '@media (max-width: 600px)': {
      maxHeight: '100%',
    },
  },
  paper: {
    width: '100%',
    maxWidth: '1300px',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#2AAFBB',
      borderRadius: '100px',
    },
  },
};

/**
 *
 * @param {object} props
 * @param {string[]} [props.selectedTreatments] - Array of selected treatments.
 * @param {string} [props.formName] - Name of the form.
 * @param {boolean} [props.scriptMode] - Whether the script mode is enabled.
 * @param {string} [props.type] - Type of the payment.
 * @param {string} [props.description] - Description of the payment.
 * @param {() => void} [props.setCollectingCardInfo] - Function to set the state of collecting card info.
 * @param {() => void} [props.formatShippingDetails] - Function to format shipping details.
 * @param {boolean} [props.pharmacyConsentRead] - Whether the pharmacy consent has been read.
 * @param {() => void} [props.updatePreferredPharmacy] - Function to update the preferred pharmacy.
 * @param {boolean} [props.isPickup] - Whether the payment is for pickup.
 * @param {boolean} [props.isPharmacyPending] - Whether the pharmacy selection is pending.
 * @param {string} [props.pharmacySelectionMode] - Mode of pharmacy selection.
 * @param {string} [props.reference] - Reference for the payment.
 * @param {string} [props.selectedPharmacy] - Selected pharmacy for the payment.
 */
const PaymentContainer = ({
  selectedTreatments = [],
  formName = '',
  scriptMode = false,
  type = '',
  description = '',
  setCollectingCardInfo = () => {},
  formatShippingDetails = () => {},
  pharmacyConsentRead = false,
  updatePreferredPharmacy = () => {},
  isPickup = false,
  isPharmacyPending = false,
  pharmacySelectionMode = '',
  reference = '',
  selectedPharmacy = '',
  preferredPharmacy = {},
  catalogue = {},
}) => {
  const analytics = useAnalytics();
  const { user } = useAuth();
  const [isCheckoutLoaded, setIsCheckoutLoaded] = useState(false);
  const [checkoutData, setCheckoutData] = useState({});
  const [shippingAlert, setShippingAlert] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [termsRead, setTermsRead] = useState(false);
  const [treatmentsToRead, setTreatmentsToRead] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [treatmentTranslator, setTreatmentTranslator] = useState({});
  const [paymentProcessor, setPaymentProcessor] = useState('');
  const [isPromoLoading, setIsPromoLoading] = useState(false);

  const handleTermsRead = useCallback(() => {
    const shippingDetails = formatShippingDetails();

    if (termsRead) {
      setTermsRead(false);
      // Make sure all shipping details have been entered
      return;
    }

    if (!scriptMode && Object.values(shippingDetails || {}).includes('')) {
      setShippingAlert('Shipping Details Missing');
      // Check that street address contains both a number and a street name
      return;
    }

    if (!scriptMode && !shippingDetails['shipping.street address']?.match(/\d/)) {
      setShippingAlert('Street Address Missing Number');
      return;
    }

    if (!scriptMode && !shippingDetails['shipping.street address']?.match(/[a-zA-Z]/)) {
      setShippingAlert('Street Address Missing Street Name');
      return;
    }
    setTermsRead(true);
    setShippingAlert('');
  }, [formatShippingDetails, scriptMode, termsRead]);

  const handlePaymentButton = useCallback(async () => {
    if (isPharmacyPending) {
      toast.warning(
        'Please select a pharmacy or set the dispatch pharmacy mode to "Auto" instead of "Manual Selection".',
      );
      return;
    }

    if (!excludedTypes.includes(type)) {
      if (scriptMode) {
        updatePreferredPharmacy();
      }
      // Lock the shipping info fields
      setCollectingCardInfo(true);
      // Add current shipping info the the update object
      const dataUpdate = formatShippingDetails();
      const generalInfoDataUpdate = formatShippingDetails(true);
      const batch = writeBatch(db);

      const acceptedAgreementsObj = treatments.reduce((acc, treatment) => {
        if (treatment.overTheCounter) {
          return acc;
        }

        const isMedicinalCannabis = formName === MEDICINAL_CANNABIS;
        const agreement = isMedicinalCannabis ? treatment.dosageForm : treatment.ingredients;
        return {
          ...acc,
          [`acceptedAgreements.${agreement}`]: Date.now(),
          ...(isMedicinalCannabis && { [`acceptedAgreements.${MEDICINAL_CANNABIS}`]: Date.now() }),
        };
      }, {});

      if (Object.keys(acceptedAgreementsObj).length) {
        const sanitizedAcceptedAgreements = processFieldData(acceptedAgreementsObj);
        batch.update(doc(db, 'patients', user?.uid), ...sanitizedAcceptedAgreements);
      }

      batch.update(doc(db, 'patients', user?.uid), dataUpdate);
      batch.update(doc(db, 'patients', user?.uid, 'general', 'information'), generalInfoDataUpdate);
      await batch.commit();
    }

    const tempPaymentProcessor = await getPaymentProcessor();

    setPaymentProcessor(tempPaymentProcessor);
    setIsCheckoutLoaded(true);
    setShowPaymentMethods(true);

    const healthCategory = getHealthCategory(formName);

    analytics.capture('checkout_flow:proceed_to_payment_click', {
      healthCategory,
      checkoutType: type,
      paymentProcessor: tempPaymentProcessor,
      $groups: { healthCategory, checkoutType: type },
    });
  }, [
    formName,
    isPharmacyPending,
    isPickup,
    pharmacySelectionMode,
    scriptMode,
    treatments,
    type,
    user?.uid,
    formatShippingDetails,
    setCollectingCardInfo,
    analytics,
  ]);

  const getTreatmentTranslator = useCallback(async () => {
    if (!selectedTreatments.length) {
      return;
    }
    const promises = selectedTreatments.map(async (value) => {
      const treatment = await getDoc(doc(db, 'catalogue', getCatalogueId(value)));
      return treatment.data();
    });

    const patientData = (await getDoc(doc(db, 'patients', user?.uid)))?.data() || {};
    const result = await Promise.all(promises);
    const toRead = result.filter(
      (item) =>
        !item.overTheCounter &&
        !Object.keys(patientData?.acceptedAgreements || {}).includes(
          formName === 'medicinal cannabis' ? item.dosageForm : item.ingredients,
        ),
    );
    const tempTreatmentTranslator = {};
    result.forEach((item) => {
      tempTreatmentTranslator[item.stripePrice] = item;
    });

    if (!toRead.length) {
      setTermsRead(true);
    }

    setTreatments(result);
    setTreatmentsToRead(toRead.map((item) => item.stripePrice));
    setTreatmentTranslator(tempTreatmentTranslator);
  }, [selectedTreatments, user, formName]);

  const paymentContextValue = useMemo(
    () => ({
      promoCode,
      treatmentsToRead,
      selectedTreatments,
      handlePaymentButton,
      isCheckoutLoaded,
      termsRead,
      description,
      checkoutData,
      setCheckoutData,
      formName,
      scriptMode,
      handleTermsRead,
      shippingAlert,
      setPromoCode,
      pharmacyConsentRead,
      isPickup,
      isPharmacyPending,
      showPaymentMethods,
      setShowPaymentMethods,
      paymentProcessor,
      setPaymentProcessor,
      treatmentTranslator,
      reference,
      isPromoLoading,
      setIsPromoLoading,
      selectedPharmacy,
      preferredPharmacy,
      catalogue,
    }),
    [
      treatmentsToRead,
      selectedTreatments,
      handlePaymentButton,
      isCheckoutLoaded,
      termsRead,
      description,
      checkoutData,
      setCheckoutData,
      formName,
      scriptMode,
      handleTermsRead,
      shippingAlert,
      promoCode,
      setPromoCode,
      pharmacyConsentRead,
      isPickup,
      isPharmacyPending,
      showPaymentMethods,
      setShowPaymentMethods,
      paymentProcessor,
      setPaymentProcessor,
      treatmentTranslator,
      reference,
      isPromoLoading,
      setIsPromoLoading,
      selectedPharmacy,
      preferredPharmacy,
      catalogue,
    ],
  );

  useEffect(() => {
    if (!reference) {
      getTreatmentTranslator();
    }
  }, [getTreatmentTranslator, reference]);

  return (
    <PaymentContext.Provider value={paymentContextValue}>
      <Grid container sx={styles.paymentContainerGrid}>
        <Paper sx={styles.paper}>
          {reference ? (
            <CustomPaymentContainer />
          ) : (
            <Grid container>
              {/* Remove the component below if script admin fee will be reimplemented */}
              <ScriptOnlyCheckout />
              <PaymentDetails />
              <PaymentProcessorContainer />
            </Grid>
          )}
        </Paper>
      </Grid>
      <PaymentMaintenance />
    </PaymentContext.Provider>
  );
};

export default PaymentContainer;
